import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FilterType } from "@/components/tables/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import type { ColumnDefinition } from "./types";

export const useUsageFilters = () => {
  const { registerFilters, getFilterValue } = useDatatableContext();
  const { searchParams } = useFiltersBySearchParams();
  const [filterableColumns, setFilterableColumns] = useState<string[]>([]);
  const registeredColumnsRef = useRef<string>("");

  useEffect(() => {
    registerFilters({
      page: { label: "Page" },
      pageSize: { label: "Page Size" },
      sortAttribute: { label: "Sort Attribute" },
      sortOrder: { label: "Sort Order" },
      costFilter: { label: "Cost" },
      costFilterMode: { label: "Cost Filter Mode" },
    });
  }, [registerFilters]);

  const costFilter = useMemo(() => getFilterValue<string>("costFilter"), [getFilterValue]);
  const costFilterMode = useMemo(
    () => (getFilterValue<string>("costFilterMode") || "gt") as "lt" | "bt" | "gt",
    [getFilterValue]
  );

  const activeFilters = useMemo(() => {
    if (filterableColumns.length === 0) return {};
    const active: Record<string, string[]> = {};
    for (const key of filterableColumns) {
      const vals = searchParams.getAll(key);
      if (vals.length > 0) active[key] = vals;
    }
    return active;
  }, [filterableColumns, searchParams]);

  // Register filterable columns dynamically once column definitions arrive.
  // Uses a ref to only re-register when column keys actually change,
  // preventing registerFilters from overwriting synced URL filter values on re-fetch.
  const registerColumns = useCallback(
    (columns: ColumnDefinition[] | undefined) => {
      if (!columns) return;
      const filterable = columns.filter(c => c.filterable);
      if (filterable.length === 0) return;

      const keys = filterable
        .map(c => c.key)
        .sort()
        .join(",");
      if (keys === registeredColumnsRef.current) return;
      registeredColumnsRef.current = keys;

      const filterConfigs: Record<string, { label: string; filterType?: FilterType }> = {};
      for (const col of filterable) {
        filterConfigs[col.key] = {
          label: col.label,
          filterType:
            col.data_type === "string" || col.data_type === "link"
              ? FilterType.API_DROPDOWN
              : FilterType.TEXT,
        };
      }
      registerFilters(filterConfigs);
      setFilterableColumns(filterable.map(c => c.key));
    },
    [registerFilters]
  );

  return { costFilter, costFilterMode, activeFilters, registerColumns };
};
